import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button, Input } from '@pijma/crypto'
import { useBaseMask, useNumericMask } from '@pijma/input'

import { sellApplicationClient } from '@api'
import { Dispatch, RootState } from '@stores'
import { isoToECurrency, TPayFormState } from '@stores/types/*'
import { charIsEnSymbol, replaceNotEnSymbol } from '@utils'
import styled from 'styled-components'

import { TBank } from '../../../../api/types/TBank'
import { banksService } from '../../../../service/payform'
import {
  SESSION_RECEIVER_ACCOUNT_HOLDER_KEY,
  SESSION_RECEIVER_ACCOUNT_NUMBER_KEY,
  SESSION_RECEIVER_BANK_KEY,
} from '../../../../stores/payform/payForm'
import { TBanksState } from '../../../../stores/types/TBanksState'
import { TPaymentGateway } from '../../../../stores/types/TPaymentGatewayState'
import { H4 } from '../../../common/header/Headings'
import { ModalSelectInput } from '../../../common/input/modalSelect/ModalSelectInput'
import { Island } from '../../../common/island'
import NavigationBar from '../../../common/navigation/NavigationBar'
import { BodyText500 } from '../../../common/text/BodyText'
import { getToSourcePath } from '../sell-provider-select-page/SellProviderSelectPage'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 20px;
`

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px;
  margin-bottom: 24px;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 20px;
  margin-top: 32px;
`

const SourceTitle = styled.div`
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;
  width: 100%;
  padding: 20px 24px 16px;
`

const NavigationBarWrapper = styled.div`
  padding: 0 4px;
  display: flex;
  width: 100%;
  align-items: start;
`

const BackContainer = styled.div`
  margin: 24px 0 0 0;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface StateProps {
  payForm: TPayFormState
  paymentGateway?: TPaymentGateway
  paymentBlocking?: boolean
  banks: TBanksState
}

interface DispatchProps {
  setReceiverAccountNumber: (receiverAccountNumber: string) => void
  setReceiverAccountHolder: (receiverAccountHolder: string) => void
  setReceiverBank: (bank: TBank) => void
  doPaymentBlocking: () => void
  resetPaymentBlocking: () => void
}

const ModalSelectInputContainer = styled.div`
  width: 100%;
  padding: 0 20px 24px;
  position: relative;
`

type Props = StateProps & DispatchProps

const SellBankMethodPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()

  const [formValid, setFormValid] = useState<boolean>(false)

  useEffect(() => {
    setFormValid(
      props.payForm.receiverAccountNumber !== undefined &&
        props.payForm.receiverAccountHolder !== undefined &&
        props.payForm.receiverAccountNumber !== '' &&
        props.payForm.receiverAccountHolder !== '' &&
        props.payForm.receiverBank !== undefined,
    )
  }, [
    props.payForm.receiverAccountNumber,
    props.payForm.receiverAccountHolder,
    props.payForm.receiverBank,
  ])

  useEffect(() => {
    sellApplicationClient
      .getBanks(props.paymentGateway?.paymentGatewayId as string)
      .then((res) => banksService.init(res))
  }, [])

  const getChosenBank = (bankId: string) => {
    return props.banks.banks.find((bank) => bank.id === bankId) as TBank
  }

  // Pijma input masks
  const accountHolderMask = useBaseMask({
    type: 'text',
    format: replaceNotEnSymbol,
    isValidInputCharacter: charIsEnSymbol,
    removeFormatting: replaceNotEnSymbol,
    value: props.payForm.receiverAccountHolder,
    onValueChange: ({ formattedValue }) => {
      props.setReceiverAccountHolder(formattedValue)
    },
  })

  const accountNumberMask = useNumericMask({
    value: props.payForm.receiverAccountNumber,
    thousandsGroupStyle: 'thousand',
    thousandSeparator: '',
    staticSeparator: true,
    allowLeadingZeros: true,
    allowNegative: false,
    onValueChange: ({ formattedValue }) => {
      props.setReceiverAccountNumber(formattedValue)
    },
  })

  const goToSumPage = () => {
    sessionStorage.setItem(
      SESSION_RECEIVER_BANK_KEY,
      JSON.stringify(props.payForm.receiverBank),
    )
    sessionStorage.setItem(
      SESSION_RECEIVER_ACCOUNT_NUMBER_KEY,
      props.payForm.receiverAccountNumber + '',
    )
    sessionStorage.setItem(
      SESSION_RECEIVER_ACCOUNT_HOLDER_KEY,
      props.payForm.receiverAccountHolder + '',
    )
    redirectTo(`../sum`)
  }

  const backLink = getToSourcePath(props.paymentGateway as TPaymentGateway)

  return (
    <Container>
      <NavigationBarWrapper>
        <NavigationBar
          paymentDisabled
          sumDisabled
          dataRedirectLink={backLink}
          showMethod
          noPaymentMethod
        />
      </NavigationBarWrapper>
      <Island size="M" bgColor="primary">
        <SourceTitle>
          <H4>
            {intl.formatMessage(
              { id: 'sellBankMethodPage.title' },
              { currency: isoToECurrency(props.paymentGateway?.currencyTo) },
            )}
          </H4>
        </SourceTitle>
        <ModalSelectInputContainer>
          {props.banks && (
            <ModalSelectInput
              defaultIcon={'/img/assets/otherBank.svg'}
              data={props.banks.banks}
              defaultText={intl.formatMessage({
                id: 'sellBankMethodPage.receiverBankDefaultText',
              })}
              searchTitle={intl.formatMessage({
                id: 'sellBankMethodPage.receiverBankSearchTitle',
              })}
              searchPlaceholder={intl.formatMessage({
                id: 'sellBankMethodPage.receiverBankSearchPlaceholder',
              })}
              onSelect={(bankId) =>
                props.setReceiverBank(getChosenBank(bankId))
              }
              selected={props.payForm.receiverBank?.id}
            />
          )}
        </ModalSelectInputContainer>
        <InputContainer>
          <Input
            {...accountNumberMask}
            label={intl.formatMessage({
              id: 'sellBankMethodPage.accountNumLabel',
            })}
            width={'100%'}
            inputMode={'decimal'}
            disabled={props.paymentBlocking}
            type={'text'}
            allowClear={true}
          />
        </InputContainer>
        <InputContainer>
          <Input
            {...accountHolderMask}
            label={intl.formatMessage({
              id: 'sellBankMethodPage.accountHolderLabel',
            })}
            width={'100%'}
            disabled={props.paymentBlocking}
            description={intl.formatMessage({
              id: 'sellBankMethodPage.accountHolderDescription',
            })}
            type={'text'}
            allowClear={true}
          />
        </InputContainer>
        <ButtonContainer>
          <Button
            width={'100%'}
            size={'m'}
            disabled={!formValid}
            onPress={() => goToSumPage()}
          >
            {intl.formatMessage({ id: 'sellSourceImpsMethodPage.goToSumPage' })}
          </Button>
        </ButtonContainer>
      </Island>
      <BackContainer onClick={() => redirectTo(backLink)}>
        <BodyText500>
          {intl.formatMessage({ id: 'sellSourceImpsMethodPage.back' })}
        </BodyText500>
      </BackContainer>
    </Container>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
  paymentGateway: state.payForm?.paymentGateway,
  paymentBlocking: state.payForm?.paymentBlocking,
  banks: state.banks,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setReceiverAccountNumber: (receiverAccountNumber: string) =>
      dispatch.payForm.set({
        receiverAccountNumber,
      }),
    setReceiverAccountHolder: (receiverAccountHolder: string) =>
      dispatch.payForm.set({
        receiverAccountHolder,
      }),
    setReceiverBank: (bank: TBank) => {
      dispatch.payForm.set({
        receiverBank: bank,
      })
    },
    doPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: true,
      }),
    resetPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: false,
      }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SellBankMethodPage)
